import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, GraduationCap, PenLine, type LucideIcon } from 'lucide-react';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import { useStudentMe } from '../../roster/hooks/useStudentMe';
import { useTutorTenantLabels } from '../../tutor/hooks/useTutorTenantLabels';
import { useLearnerCurriculum } from '../../curriculum/hooks/useLearnerCurriculum';
import { resolveLearnerTrackContext } from '../../student-nav/studentPocNavigation';
import { buildExpositoryWritingStudioUrl } from '../../tutor/malayExpositoryWritingRoute';
import { ROUTES } from '../../../utils/constants';
import { usePrefetchReadingComprehension } from '../../reading/usePrefetchReadingComprehension';

type QuickAction = {
  id: string;
  labelKey: string;
  route: string;
  icon: LucideIcon;
};

export function MalayQuickActions() {
  const { t } = useTranslation();
  const { isSchoolFeatureEnabled, loading: featureAccessLoading } = useFeatureAccess();
  const { labels, loading: labelsLoading } = useTutorTenantLabels();
  const { curriculum, loading: curriculumLoading } = useLearnerCurriculum();
  const { grade, isRosterLoading } = useStudentMe();
  const { track } = resolveLearnerTrackContext({
    curriculum,
    legacyTenantIsMalay: labels.isMalay,
    curriculumLoading,
  });

  const actions = useMemo(() => {
    if (
      featureAccessLoading ||
      labelsLoading ||
      isRosterLoading ||
      track !== 'malay'
    ) {
      return [] as QuickAction[];
    }

    const items: QuickAction[] = [];
    if (isSchoolFeatureEnabled('ai_tutor')) {
      items.push({
        id: 'ai-tutor',
        labelKey: 'dashboard.studentHome.malayQuickActions.aiTutor',
        route: ROUTES.STUDENT_TAMIL_TUTOR,
        icon: GraduationCap,
      });
    }

    const gradeNum = typeof grade === 'number' ? grade : parseInt(String(grade ?? ''), 10);
    if (
      isSchoolFeatureEnabled('reading_comprehension') &&
      Number.isFinite(gradeNum) &&
      gradeNum === 7
    ) {
      items.push({
        id: 'reading',
        labelKey: 'dashboard.studentHome.malayQuickActions.reading',
        route: ROUTES.STUDENT_READING_COMPREHENSION,
        icon: BookOpen,
      });
    }

    if (isSchoolFeatureEnabled('narrative_writing')) {
      items.push({
        id: 'narrative',
        labelKey: 'dashboard.studentHome.malayQuickActions.narrative',
        route: ROUTES.STUDENT_NARRATIVE_WRITING,
        icon: PenLine,
      });
    }

    if (isSchoolFeatureEnabled('essay_writing')) {
      items.push({
        id: 'expository',
        labelKey: 'dashboard.studentHome.malayQuickActions.expository',
        route: buildExpositoryWritingStudioUrl(),
        icon: PenLine,
      });
    }

    return items;
  }, [
    featureAccessLoading,
    labelsLoading,
    isRosterLoading,
    track,
    isSchoolFeatureEnabled,
    grade,
  ]);

  usePrefetchReadingComprehension(actions.some((action) => action.id === 'reading'));

  if (featureAccessLoading || labelsLoading || track !== 'malay') return null;
  if (actions.length === 0) return null;

  return (
    <section className="mb-6" aria-labelledby="malay-quick-actions-heading">
      <h2
        id="malay-quick-actions-heading"
        className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-600 dark:text-slate-400"
      >
        {t('dashboard.studentHome.malayQuickActions.title')}
      </h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <Link
              key={action.id}
              to={action.route}
              className="flex min-h-[88px] flex-col items-center justify-center gap-2 rounded-2xl border border-purple-200/70 bg-white/90 px-3 py-4 text-center shadow-sm transition hover:border-purple-300 hover:bg-purple-50/80 dark:border-purple-500/25 dark:bg-gray-900/50 dark:hover:bg-purple-950/30"
              data-testid={`malay-quick-action-${action.id}`}
            >
              <Icon className="h-5 w-5 text-purple-600 dark:text-purple-300" aria-hidden />
              <span className="text-xs font-semibold leading-snug text-slate-800 dark:text-slate-100">
                {t(action.labelKey)}
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
